import { Controller } from "@hotwired/stimulus"

// A horizontal row that the page "pins": the section grows tall, its stage
// sticks to the viewport, and scrolling down slides the row sideways until its
// last item is in view — then the page moves on. Desktop only, and only for
// people who haven't asked for less motion; everyone else keeps the row as a
// normal swipeable strip (the markup works untouched without this).
//
// Usage:
//   <section data-controller="pinned-run" data-action="focusin->pinned-run#reveal">
//     <div data-pinned-run-target="stage">
//       <ul data-pinned-run-target="track"><li>…</li></ul>
//     </div>
//   </section>
//
// While pinned the section has .is-pinned; the CSS makes the stage sticky and
// stops the track scrolling on its own.
export default class extends Controller {
  static targets = ["stage", "track"]

  connect() {
    this.query    = window.matchMedia("(min-width: 901px) and (prefers-reduced-motion: no-preference)")
    this.onChange = () => this.setup()
    this.onScroll = () => {
      if (this.frame) return
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update() })
    }

    this.query.addEventListener("change", this.onChange)
    window.addEventListener("resize", this.onChange)
    window.addEventListener("scroll", this.onScroll, { passive: true })
    // Images arriving late change the row's width.
    window.addEventListener("load", this.onChange)
    this.setup()
  }

  disconnect() {
    this.query.removeEventListener("change", this.onChange)
    window.removeEventListener("resize", this.onChange)
    window.removeEventListener("scroll", this.onScroll)
    window.removeEventListener("load", this.onChange)
    this.teardown()
  }

  // Tabbing to a print that is still off to the side scrolls the page to the
  // point where the row has slid it into view, so keyboard users can reach it.
  reveal(event) {
    if (!this.pinned) return
    const item = event.target.closest("li")
    if (!item) return

    const start  = parseFloat(getComputedStyle(this.trackTarget).paddingLeft) || 0
    const offset = Math.min(Math.max(item.offsetLeft - start, 0), this.distance)
    const top    = this.element.getBoundingClientRect().top + window.scrollY
    window.scrollTo({ top: top + (offset / this.distance) * this.span(), behavior: "instant" })
  }

  // private

  setup() {
    this.teardown()
    if (!this.query.matches) return

    this.element.classList.add("is-pinned")
    const last  = this.trackTarget.lastElementChild
    const end   = parseFloat(getComputedStyle(this.trackTarget).paddingRight) || 0
    this.distance = last
      ? Math.ceil(last.getBoundingClientRect().right + end - this.stageTarget.getBoundingClientRect().right)
      : 0

    // Everything already fits: nothing to slide, so don't hold the page.
    if (this.distance <= 0) return this.teardown()

    this.pinned = true
    this.element.style.height = `${window.innerHeight + this.distance}px`
    this.update()
  }

  teardown() {
    this.pinned = false
    this.element.classList.remove("is-pinned")
    this.element.style.height = ""
    this.trackTarget.style.transform = ""
  }

  span() {
    return this.element.offsetHeight - window.innerHeight
  }

  update() {
    if (!this.pinned) return
    const progress = Math.min(Math.max(-this.element.getBoundingClientRect().top / this.span(), 0), 1)
    this.trackTarget.style.transform = `translate3d(${-progress * this.distance}px, 0, 0)`
  }
}
